import { motion } from 'framer-motion';
import labImage from '../assets/work-in-lab.webp';
import lab1 from '../assets/lab1.webp';
import lab2 from '../assets/lab2.webp';
import lab3 from '../assets/lab3.webp';
import achievementsSourodipto from '../assets/achievements_sourodipto.jpeg';
import biofuelImage from '../assets/Biofuel production.jpg';
import membraneImage from '../assets/Mixed matrix membrane and fiber production.jpg';
import membraneImageCopy from '../assets/Mixed matrix membrane and fiber production copy.jpeg';
import wasteImage from '../assets/Utilization of agro-industrial solid waste-A circular economy approach.jpg';
import wasteImageCopy from '../assets/Utilization of agro-industrial solid waste-A circular economy approach copy.jpeg';
import React from 'react';

// Add a simple LinkedIn SVG icon component
const LinkedInIcon = () => (
    <svg className="w-5 h-5 text-blue-700 dark:text-blue-400 inline-block ml-1" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.27c-.97 0-1.75-.79-1.75-1.76s.78-1.76 1.75-1.76 1.75.79 1.75 1.76-.78 1.76-1.75 1.76zm13.5 11.27h-3v-5.6c0-1.34-.03-3.07-1.87-3.07-1.87 0-2.16 1.46-2.16 2.97v5.7h-3v-10h2.89v1.36h.04c.4-.75 1.37-1.54 2.82-1.54 3.01 0 3.57 1.98 3.57 4.56v5.62z" />
    </svg>
);

// Add Modal Component
const ImageModal = ({ isOpen, onClose, image, alt }) => {
    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="relative max-w-4xl w-full mx-4"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
                    aria-label="Close modal"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <img
                    src={image}
                    alt={alt}
                    className="w-full h-auto rounded-lg shadow-2xl"
                />
            </motion.div>
        </motion.div>
    );
};

export default function Labs() {
    const equipment = [
        "Fixed bed pyrolysis reactor",
        "Rotary evaporator",
        "Orbital shaking incubator",
        "Multiphotometer (COD, Color, pH etc)",
        "UV spectroscopy",
        "Karl-fisher titrator",
        "Fume hood",
        "Supercritical fluid extraction setup"
    ];

    // Split team members
    const completedPhD = [
        {
            name: "Dr. Sourodipto Modak",
            role: "PhD Scholar (Completed)",
            image: "/sourodipto.jpeg",
            research: "Bio-oil Production from Waste Materials",
            linkedin: "https://www.linkedin.com/in/sourodipto-modak-509833109/"
        }
    ];

    const currentTeam = [
        {
            name: "Ms. Kohnoor Bano",
            role: "PhD Scholar",
            image: "/kohnoor.jpeg",
            research: "Catalysis and Biofuel Production",
            linkedin: "https://www.linkedin.com/in/kohnoor-bano-14ab07236/"
        },
        {
            name: "Mr. Geetesh Kumar",
            role: "Research Assistant",
            image: "geetesh.jpeg",
            research: "Advanced Separation Technologies",
            linkedin: "https://www.linkedin.com/in/geeteshkr/"
        },
        {
            name: "Mr. Syed Ali",
            role: "Research Assistant",
            image: "/team.png",
            research: "Advanced Separation Technologies"
        },
        {
            name: "Undergraduate Researchers",
            role: "Project Students",
            image: "/team.png",
            research: "Various Research Projects"
        }
    ];

    const labImages = [
        { src: labImage, alt: "Work in Lab" },
        { src: achievementsSourodipto, alt: "Dr. Sourodipto's Achievements" },
        { src: lab1, alt: "Lab 1" },
        { src: lab3, alt: "Lab 3" },
        { src: lab2, alt: "Lab 2" },
    ];

    const [current, setCurrent] = React.useState(0);
    const [membraneCurrent, setMembraneCurrent] = React.useState(0);
    const [wasteCurrent, setWasteCurrent] = React.useState(0);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState({ src: '', alt: '' });

    const prevSlide = () => setCurrent((prev) => (prev === 0 ? labImages.length - 1 : prev - 1));
    const nextSlide = () => setCurrent((prev) => (prev === labImages.length - 1 ? 0 : prev + 1));

    const prevMembraneSlide = () => setMembraneCurrent((prev) => (prev === 0 ? 1 : prev - 1));
    const nextMembraneSlide = () => setMembraneCurrent((prev) => (prev === 1 ? 0 : prev + 1));

    const prevWasteSlide = () => setWasteCurrent((prev) => (prev === 0 ? 1 : prev - 1));
    const nextWasteSlide = () => setWasteCurrent((prev) => (prev === 1 ? 0 : prev + 1));

    const openModal = (src, alt) => {
        setSelectedImage({ src, alt });
        setModalOpen(true);
    };

    // Auto-slide effect for main lab slider
    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrent((prev) => (prev === labImages.length - 1 ? 0 : prev + 1));
        }, 3000); // 3 seconds

        return () => clearInterval(interval);
    }, [labImages.length]);

    // Auto-slide effect for membrane slider
    React.useEffect(() => {
        const interval = setInterval(() => {
            setMembraneCurrent((prev) => (prev === 1 ? 0 : prev + 1));
        }, 4000); // 4 seconds

        return () => clearInterval(interval);
    }, []);

    // Auto-slide effect for waste slider
    React.useEffect(() => {
        const interval = setInterval(() => {
            setWasteCurrent((prev) => (prev === 1 ? 0 : prev + 1));
        }, 4000); // 4 seconds

        return () => clearInterval(interval);
    }, []);

    return (
        <motion.section
            id="labs"
            className="max-w-5xl mx-auto py-8 px-8 bg-gradient-to-tr from-gray-50 to-gray-200 dark:from-gray-900 dark:to-gray-800 rounded-lg shadow-xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
        >
            <div className="relative mb-8">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-white text-center">
                    Energy and Environmental Sustainability Laboratory
                </h2>
                <div className="flex justify-center mt-2">
                    <span className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 text-base font-medium">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span>F 103F, F block, Shiv Nadar University</span>
                    </span>
                </div>
                {/* <div className="absolute w-32 h-1 bg-blue-600 bottom-0 left-1/2 transform -translate-x-1/2 mt-4"></div> */}
            </div>

            <div className="grid md:grid-cols-5 gap-8 mb-8">
                <div className="relative h-64 md:h-80 flex items-center justify-center overflow-hidden group md:col-span-3">
                    {/* Left Arrow */}
                    <button
                        onClick={prevSlide}
                        className="absolute left-2 z-10 bg-white/90 hover:bg-blue-600 hover:text-white rounded-full p-2 shadow transition-all duration-300 opacity-80 group-hover:opacity-100"
                        aria-label="Previous image"
                        style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    {/* Image with animation */}
                    <div className="w-full h-full relative">
                        {labImages.map((img, idx) => (
                            <motion.div
                                key={img.alt}
                                className="absolute inset-0"
                                initial={{ opacity: 0, x: idx > current ? 100 : -100 }}
                                animate={{
                                    opacity: idx === current ? 1 : 0,
                                    x: idx === current ? 0 : idx > current ? 100 : -100,
                                    zIndex: idx === current ? 1 : 0,
                                }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                            >
                                <img
                                    src={img.src}
                                    alt={img.alt}
                                    loading="lazy"
                                    className="w-full h-full object-cover rounded-lg shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    style={{ aspectRatio: '16/9' }}
                                    onClick={() => openModal(img.src, img.alt)}
                                />
                            </motion.div>
                        ))}
                    </div>
                    {/* Right Arrow */}
                    <button
                        onClick={nextSlide}
                        className="absolute right-2 z-10 bg-white/90 hover:bg-blue-600 hover:text-white rounded-full p-2 shadow transition-all duration-300 opacity-80 group-hover:opacity-100"
                        aria-label="Next image"
                        style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    {/* Dots */}
                    <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
                        {labImages.map((_, idx) => (
                            <motion.button
                                key={idx}
                                onClick={() => setCurrent(idx)}
                                className={`w-3 h-3 rounded-full border-2 border-blue-300 transition-all duration-300
                                    ${current === idx ? 'bg-blue-600 scale-125 border-blue-600 shadow' : 'bg-white hover:bg-blue-200'}`}
                                aria-label={`Go to slide ${idx + 1}`}
                                whileTap={{ scale: 0.9 }}
                                whileHover={{ scale: 1.15 }}
                            />
                        ))}
                    </div>
                </div>

                <div className="space-y-4 md:col-span-2">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        The focus of our research group lies in the investigation of ecological technologies to create energy efficient and clean environment. We currently focus on the production of Bio-oil from agro-industrial solid waste and food waste using thermochemical conversion methods.
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        Our research also encompasses advanced separation technologies including membrane separation, Supercritical fluid extraction, and Supercritical fluid drying, along with Simulation and Modelling of these processes.
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white dark:bg-surface-dark rounded-xl p-6 shadow-lg">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Laboratory Equipment</h3>
                    <ul className="space-y-2">
                        {equipment.map((item, index) => (
                            <li key={index} className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-gray-700 dark:text-gray-300">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="bg-white dark:bg-surface-dark rounded-xl p-6 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">Research Team</h3>
                    </div>

                    {/* Completed PhD Section */}
                    <div className="mb-4">
                        {/* <h4 className="text-lg font-semibold text-gray-700 mb-2">Completed PhD</h4> */}
                        <div className="grid gap-3">
                            {completedPhD.map((member, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center space-x-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
                                >
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-tr from-blue-100 to-blue-200 border-2 border-blue-500/20">
                                            {member.image ? (
                                                <img
                                                    src={member.image}
                                                    alt={member.name}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                                            {member.name}
                                            {member.linkedin && (
                                                <a
                                                    href={member.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`LinkedIn profile of ${member.name}`}
                                                >
                                                    <LinkedInIcon />
                                                </a>
                                            )}
                                        </h4>
                                        <p className="text-blue-600 dark:text-blue-400 font-medium">{member.role}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{member.research}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Divider */}
                    <hr className="my-4 border-t border-gray-300 dark:border-gray-700" />

                    {/* Current Team Section */}
                    <div>
                        {/* <h4 className="text-lg font-semibold text-gray-700 mb-2">Current Team</h4> */}
                        <div className="grid gap-3">
                            {currentTeam.map((member, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center space-x-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
                                >
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-tr from-blue-100 to-blue-200 border-2 border-blue-500/20">
                                            {member.image ? (
                                                <img
                                                    src={member.image}
                                                    alt={member.name}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                                            {member.name}
                                            {member.linkedin && (
                                                <a
                                                    href={member.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`LinkedIn profile of ${member.name}`}
                                                >
                                                    <LinkedInIcon />
                                                </a>
                                            )}
                                        </h4>
                                        <p className="text-blue-600 dark:text-blue-400 font-medium">{member.role}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{member.research}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Major Research Section */}
            {/* <div className="mt-4 mb-4 bg-gradient-to-br from-white to-gray-200 rounded-2xl p-8"> */}
            <div className="text-center mb-4 mt-8">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Major Research Work</h2>
                <div className="w-24 h-1 bg-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Exploring innovative solutions in chemical engineering and sustainable technologies</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Research Card 1 */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white dark:bg-surface-dark rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    <div className="relative h-56 p-2">
                        <img
                            src={biofuelImage}
                            alt="Biofuel Production Research"
                            loading="lazy"
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity rounded-lg"
                            onClick={() => openModal(biofuelImage, "Biofuel Production Research")}
                        />
                    </div>
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0">
                                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                    </svg>
                                </div>
                            </div>
                            <h4 className="text-xl font-semibold text-gray-800 dark:text-white">Bio-oil Production</h4>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Development of sustainable methods for bio-oil production from agricultural waste using advanced pyrolysis techniques.
                        </p>
                    </div>
                </motion.div>

                {/* Research Card 2 - With Image Slider */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="bg-white dark:bg-surface-dark rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    <div className="relative h-56 p-2">
                        <div className="w-full h-full relative">
                            {[membraneImage, membraneImageCopy].map((img, idx) => (
                                <motion.div
                                    key={idx}
                                    className="absolute inset-0"
                                    initial={{ opacity: 0, x: idx > membraneCurrent ? 100 : -100 }}
                                    animate={{
                                        opacity: idx === membraneCurrent ? 1 : 0,
                                        x: idx === membraneCurrent ? 0 : idx > membraneCurrent ? 100 : -100,
                                        zIndex: idx === membraneCurrent ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                >
                                    <img
                                        src={img}
                                        alt={`Membrane Separation Research ${idx + 1}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity rounded-lg"
                                        onClick={() => openModal(img, `Membrane Separation Research ${idx + 1}`)}
                                    />
                                </motion.div>
                            ))}
                        </div>
                        <button
                            onClick={prevMembraneSlide}
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-blue-600 hover:text-white rounded-full p-2 shadow transition-all duration-300 opacity-80 hover:opacity-100"
                            aria-label="Previous image"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={nextMembraneSlide}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-blue-600 hover:text-white rounded-full p-2 shadow transition-all duration-300 opacity-80 hover:opacity-100"
                            aria-label="Next image"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0">
                                <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-lg">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </div>
                            </div>
                            <h4 className="text-xl font-semibold text-gray-800 dark:text-white">Membrane Separation</h4>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Innovative membrane technologies for efficient separation and purification of bio-based products.
                        </p>
                    </div>
                </motion.div>

                {/* Research Card 3 - With Image Slider */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-white dark:bg-surface-dark rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    <div className="relative h-56 p-2">
                        <div className="w-full h-full relative">
                            {[wasteImage, wasteImageCopy].map((img, idx) => (
                                <motion.div
                                    key={idx}
                                    className="absolute inset-0"
                                    initial={{ opacity: 0, x: idx > wasteCurrent ? 100 : -100 }}
                                    animate={{
                                        opacity: idx === wasteCurrent ? 1 : 0,
                                        x: idx === wasteCurrent ? 0 : idx > wasteCurrent ? 100 : -100,
                                        zIndex: idx === wasteCurrent ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                >
                                    <img
                                        src={img}
                                        alt={`Waste Valorization Research ${idx + 1}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity rounded-lg"
                                        onClick={() => openModal(img, `Waste Valorization Research ${idx + 1}`)}
                                    />
                                </motion.div>
                            ))}
                        </div>
                        <button
                            onClick={prevWasteSlide}
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-blue-600 hover:text-white rounded-full p-2 shadow transition-all duration-300 opacity-80 hover:opacity-100"
                            aria-label="Previous image"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={nextWasteSlide}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-blue-600 hover:text-white rounded-full p-2 shadow transition-all duration-300 opacity-80 hover:opacity-100"
                            aria-label="Next image"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0">
                                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                    </svg>
                                </div>
                            </div>
                            <h4 className="text-xl font-semibold text-gray-800 dark:text-white">Waste Valorization</h4>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400">
                          Sustainable conversion of industrial and agricultural waste into valuable products and energy.
                        </p>
                    </div>
                </motion.div>

                {/* Remaining cards with updated styling */}
                {/* Research Card 4 */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="bg-white dark:bg-surface-dark rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                        </div>
                        <h4 className="text-xl font-semibold text-gray-800 dark:text-white">Supercritical Fluid Technology</h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Development of supercritical fluid extraction and drying processes for sustainable material processing.
                    </p>
                </motion.div>

                {/* Research Card 5 */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="bg-white dark:bg-surface-dark rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/40 rounded-lg">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </div>
                        </div>
                        <h4 className="text-xl font-semibold text-gray-800 dark:text-white">Catalysis Research</h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Development of novel catalysts for efficient conversion of biomass to valuable products.
                    </p>
                </motion.div>

                {/* Research Card 6 */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="bg-white dark:bg-surface-dark rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-12 h-12 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
                                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                        </div>
                        <h4 className="text-xl font-semibold text-gray-800 dark:text-white">Process Modeling</h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Advanced simulation and modeling of thermochemical conversion processes for optimization.
                    </p>
                </motion.div>
            </div>
            {/* </div> */}

            {/* Image Modal */}
            <ImageModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                image={selectedImage.src}
                alt={selectedImage.alt}
            />
        </motion.section>
    );
} 